import React, { useState } from 'react';
import { CheckCircle, Loader2, Plus, X } from 'lucide-react';
import { INDUSTRIES, industryLabel } from './setupConstants';
import { AutoDiscoverExtensions } from './AutoDiscoverExtensions';

// ── Manual extension entry fallback ───────────────────────────────────────────
const ManualExtensionEntry = ({ form, toggleExtension, setExtClassification }) => {
  const [manual, setManual] = useState('');
  return (
    <div className="space-y-2">
      <p className="text-white/30 text-xs">Extensions could not be fetched automatically. Enter manually:</p>
      <div className="flex gap-2">
        <input
          type="text"
          value={manual}
          onChange={e => setManual(e.target.value)}
          placeholder="Extension number"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
        />
        <button
          onClick={() => {
            const val = manual.trim();
            if (val) { toggleExtension(val); setManual(''); }
          }}
          className="px-3 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-400"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {form.extensions_monitored.map(ext => (
        <div key={ext} className="flex items-center gap-3 px-3 py-2 bg-white/5 rounded-lg text-sm text-white/60">
          <span className="flex-1">Ext {ext}</span>
          <select
            value={form.extension_classifications[ext] || ''}
            onChange={e => setExtClassification(ext, e.target.value)}
            className="bg-[#0A0F1C] border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-teal-500/50"
          >
            <option value="">Use domain default</option>
            {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          <button onClick={() => toggleExtension(ext)}>
            <X className="w-4 h-4 text-white/30 hover:text-white" />
          </button>
        </div>
      ))}
    </div>
  );
};

// ── Extension row ─────────────────────────────────────────────────────────────
const ExtensionRow = ({ ext, isSelected, assignedType, toggleExtension, setExtClassification }) => (
  <div
    className={`rounded-lg border transition-colors ${
      isSelected ? 'border-teal-500/50 bg-teal-500/5' : 'border-white/10 bg-white/[0.02]'
    }`}
  >
    <div className="flex items-center gap-3 px-4 py-3">
      <button
        onClick={() => toggleExtension(ext.extension)}
        className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
          isSelected ? 'bg-teal-500 border-teal-500' : 'border-white/20 hover:border-white/40'
        }`}
        data-testid={`ext-${ext.extension}`}
      >
        {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{ext.name || ext.extension}</p>
        <p className="text-white/30 text-xs">Ext {ext.extension}</p>
      </div>
      {isSelected && (
        <select
          value={assignedType}
          onChange={e => setExtClassification(ext.extension, e.target.value)}
          className="bg-[#0A0F1C] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-teal-500/50 min-w-[170px]"
          data-testid={`ext-type-${ext.extension}`}
        >
          <option value="">Use domain default</option>
          {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
        </select>
      )}
    </div>
    {isSelected && assignedType && (
      <div className="px-12 pb-2.5">
        <span className="text-xs bg-teal-500/10 text-teal-400 border border-teal-500/20 px-2 py-0.5 rounded">
          Coaching: {industryLabel(assignedType)}
        </span>
      </div>
    )}
  </div>
);

// ── Step 3 main ───────────────────────────────────────────────────────────────
export const StepExtensions = ({
  form, setForm,
  availableExtensions, loadingExtensions,
  toggleExtension, setExtClassification,
  domainId, token,
}) => {
  const [search, setSearch] = useState('');

  if (loadingExtensions) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 text-white/20 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-white/50 text-sm">
          Select extensions to monitor and assign a <strong className="text-white/70">coaching type</strong> to each one. Calls from that extension will be graded against the matching knowledge base.
        </p>
        <p className="text-white/30 text-xs mt-1">
          If no coaching type is assigned, the domain's default industry (<span className="text-teal-400">{industryLabel(form.industry)}</span>) is used.
        </p>
      </div>

      {domainId && (
        <AutoDiscoverExtensions
          domainId={domainId}
          token={token}
          monitoredExtensions={form.extensions_monitored}
          classifications={form.extension_classifications}
          toggleExtension={toggleExtension}
          setExtClassification={setExtClassification}
        />
      )}

      {availableExtensions.length > 0 ? (
        <>
          <div className="flex items-center justify-between text-xs text-white/30 mb-2">
            <span>{form.extensions_monitored.length} of {availableExtensions.length} selected</span>
            <div className="flex gap-3">
              <button
                className="text-teal-400 hover:text-teal-300"
                onClick={() => setForm(f => ({ ...f, extensions_monitored: availableExtensions.map(e => e.extension) }))}
              >
                Select All
              </button>
              <button
                className="text-white/40 hover:text-white/60"
                onClick={() => setForm(f => ({ ...f, extensions_monitored: [], extension_classifications: {} }))}
              >
                Clear
              </button>
            </div>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or extension number..."
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50 mb-2"
          />
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {availableExtensions
              .filter(ext =>
                !search ||
                ext.extension.includes(search) ||
                (ext.name || '').toLowerCase().includes(search.toLowerCase())
              )
              .map(ext => (
                <ExtensionRow
                  key={ext.extension}
                  ext={ext}
                  isSelected={form.extensions_monitored.includes(ext.extension)}
                  assignedType={form.extension_classifications[ext.extension] || ''}
                  toggleExtension={toggleExtension}
                  setExtClassification={setExtClassification}
                />
              ))}
          </div>
        </>
      ) : (
        <ManualExtensionEntry form={form} toggleExtension={toggleExtension} setExtClassification={setExtClassification} />
      )}
    </div>
  );
};
